import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
// eslint-disable-next-line import/named
import {
  getProductions as getProductionsAPI,
  getAggregations as getAggreationsAPI,
  getPerformances as getPerformancesAPI,
  getCitiesAggregations as getCitiesAggregationsAPI,
} from '../API';
import { PRODUCTION_VIEW_TYPES } from '../../constants/consts';
import { removeWithTranslationProps, fetchAbortController, getDateDifference, getValidDateRange } from '../common';
import { ADMIN_COMPANY, PAGINATION_LIMIT_1000 } from './constants';

/**
 * GET/SET defaults Start/End dates for Productions
 * @param query
 * @param wasListLoaded
 * @returns {{dayTo: string, dayFrom: string}}
 */
export const getDates = query => {
  const dayFromQuery = get(query, 'date_from');
  const dayToQuery = get(query, 'date_to');
  return getValidDateRange(dayFromQuery, dayToQuery);
};

/**
 * GET Productions
 * @param queryParams
 * @param pagination
 * @returns {Promise<*>}
 */
export const getProductions = async (queryParams, pagination) => {
  const strippedQuery = removeWithTranslationProps(queryParams);
  const { sort } = strippedQuery;
  delete strippedQuery.sort;
  const { dayFrom, dayTo } = getDates(strippedQuery);

  return getProductionsAPI(
    {
      ...strippedQuery,
      date_from: dayFrom,
      date_to: dayTo,
      added_by: ADMIN_COMPANY,
      approved_by: ADMIN_COMPANY,
      sort,
    },
    pagination,
  ).then(response => response.data);
};

export const getPerformances = async (queryParams, pagination) => {
  const strippedQuery = removeWithTranslationProps(queryParams);
  const { sort } = strippedQuery;
  delete strippedQuery.sort;
  const { dayFrom, dayTo } = getDates(strippedQuery);

  return getPerformancesAPI(
    {
      ...strippedQuery,
      date_from: dayFrom,
      date_to: dayTo,
      added_by: ADMIN_COMPANY,
      approved_by: ADMIN_COMPANY,
      sort,
    },
    pagination,
  ).then(response => response.data);
};

export const getAggregations = async (apiEndpoint, aggregationParams, pagination) => {
  const abortController = fetchAbortController();
  const signal = abortController ? abortController.signal : null;
  return getAggreationsAPI(
    apiEndpoint,
    { ...aggregationParams, added_by: ADMIN_COMPANY, approved_by: ADMIN_COMPANY },
    pagination,
    signal,
  ).then(response => response.data);
};

export const getFilteredQuery = (aggregations, pageView) => {
  const filteredQuery = JSON.parse(JSON.stringify(aggregations));
  if (pageView === 'production') {
    filteredQuery.aggregation_type = 'city_id';
    filteredQuery.aggregation_query = '';
  } else {
    filteredQuery.aggregation_type = 'on_date';
  }
  return filteredQuery;
};

export const groupByCities = cities => {
  const countryWithCities = cities.reduce((result, item) => {
    // eslint-disable-next-line no-multi-assign,no-param-reassign
    const country = (result[item.country] = result[item.country] || {});
    // eslint-disable-next-line no-multi-assign
    const city = (country[item.name] = country[item.name] || []);
    city.push(item);
    return result;
  }, {});

  const orderedCountryWithCities = Object.keys(countryWithCities)
    .sort()
    .reduce((obj, key) => {
      obj[key] = countryWithCities[key];
      return obj;
    }, {});

  return orderedCountryWithCities;
};

/**
 * GET Performance Cities
 * @param queryParams
 * @param pagination
 * @returns {Promise<*>}
 */
export const getPerformanceCities = async (apiEndpoint, aggregationParams, pagination) => {
  // eslint-disable-next-line no-param-reassign
  delete aggregationParams.on_date;

  return getCitiesAggregationsAPI(
    apiEndpoint,
    {
      ...aggregationParams,
      aggregation_type: 'city_id',
      aggregation_query: '',
      added_by: ADMIN_COMPANY,
      approved_by: ADMIN_COMPANY,
    },
    pagination,
  ).then(async response => {
    const cities = response?.data?.data;
    // check cities length
    // eslint-disable-next-line eqeqeq
    if (cities.length == 0) {
      return cities;
    }
    // TODO: Change the limit for a better user experience
    const perfromanceResponse = await getPerformancesAPI(
      { ...aggregationParams, added_by: ADMIN_COMPANY, approved_by: ADMIN_COMPANY },
      PAGINATION_LIMIT_1000,
    );
    // map data to cities
    const performances = get(perfromanceResponse, 'data.data', []);
    // eslint-disable-next-line eqeqeq
    if (performances.length == 0) {
      return cities;
    }
    // transform performances
    // TODO: add a transformer after identifying all changes
    const transformedPerformances = performances.map(performance => {
      // eslint-disable-next-line no-param-reassign
      performance.companies = [performance.company];
      // eslint-disable-next-line no-param-reassign
      performance.venues = [performance.venue];

      return performance;
    });
    // map the performances to cities
    const groupedPerformances = groupBy(transformedPerformances, o => get(o, 'city.id', null));

    const groupedCities = [];
    cities.forEach(city => {
      const cityPerformances = groupedPerformances[city.id];
      if (cityPerformances && cityPerformances.length > 0) {
        // eslint-disable-next-line no-param-reassign
        city.performances = groupedPerformances[city.id];
        groupedCities.push(city);
      }
    });
    groupedCities.sort((a, b) => a.country.localeCompare(b.country) || b.name - a.name);
    return groupedCities;
  });
};

export const getPageView = (toDate, fromDate) => {
  if (!toDate || !fromDate) {
    return PRODUCTION_VIEW_TYPES.PERFORMANCE;
  }
  if (fromDate && !toDate) {
    return PRODUCTION_VIEW_TYPES.PRODUCTION;
  }
  const dateDifference = getDateDifference(toDate, fromDate);
  return dateDifference > 30 ? PRODUCTION_VIEW_TYPES.PRODUCTION : PRODUCTION_VIEW_TYPES.PERFORMANCE;
};

export const groupSearchResults = (searchResults, pageView) =>
  pageView === 'production' ? groupByCities(get(searchResults, 'data', []), pageView) : get(searchResults, 'data', []);
